import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import ButtonBase from '@mui/material/ButtonBase';
import { useLanguage, FLAG_ES, FLAG_EN } from 'context/LanguageContext';

/**
 * Pill con dos banderas circulares para cambiar el idioma.
 * - darkMode: adapta colores al fondo oscuro (login panel) vs. claro (dashboard/ajustes).
 * - sx: estilos extra opcionales sobre el contenedor pill.
 */

const OPTIONS_BASE = [
  { code: 'es', src: FLAG_ES, ariaLabel: 'Español' },
  { code: 'en', src: FLAG_EN, ariaLabel: 'English' },
];

function LanguageSwitcher({ darkMode, sx }) {
  const { lang, setLanguage, ipLang } = useLanguage();

  // El orden se basa en la IP detectada, no en la selección actual
  // Usuario en USA → EN primero siempre; resto → ES primero siempre
  const options = ipLang === 'en' ? [...OPTIONS_BASE].reverse() : OPTIONS_BASE;

  // aria-label del grupo en el idioma actual
  const groupLabel = lang === 'en' ? 'Language' : 'Idioma';

  /* ── Tokens de color según dark / light ─────────────────────────── */
  const pillBorderColor = darkMode
    ? 'rgba(255,255,255,0.11)'
    : 'rgba(17,17,17,0.09)';

  const pillBg = darkMode
    ? 'rgba(255,255,255,0.05)'
    : 'rgba(17,17,17,0.045)';

  const pillShadow = darkMode
    ? 'inset 0 1px 0 rgba(255,255,255,0.04), 0 1px 3px rgba(0,0,0,0.32)'
    : 'inset 0 1px 0 rgba(255,255,255,0.65), 0 1px 3px rgba(0,0,0,0.07)';

  const activeBg = darkMode
    ? 'linear-gradient(145deg, rgba(255,255,255,0.14) 0%, rgba(255,255,255,0.08) 100%)'
    : 'linear-gradient(145deg, #ffffff 0%, #f3f6fa 100%)';

  const activeShadow = darkMode
    ? '0 2px 8px rgba(0,0,0,0.38), inset 0 1px 0 rgba(255,255,255,0.14)'
    : '0 2px 7px rgba(0,0,0,0.13), inset 0 1px 0 rgba(255,255,255,0.95)';

  return (
    <Box
      role="group"
      aria-label={groupLabel}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.15rem',
        p: '0.18rem',
        borderRadius: '999px',
        border: `1px solid ${pillBorderColor}`,
        bgcolor: pillBg,
        boxShadow: pillShadow,
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        ...sx,
      }}
    >
      {options.map(({ code, src, ariaLabel }) => {
        const active = lang === code;
        return (
          <ButtonBase
            key={code}
            component="button"
            type="button"
            onClick={() => setLanguage(code)}
            disableRipple
            aria-pressed={active}
            aria-label={ariaLabel}
            title={ariaLabel}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: '2.25rem',
              minHeight: '2.25rem',
              borderRadius: '999px',
              background: active ? activeBg : 'transparent',
              boxShadow: active ? activeShadow : 'none',
              opacity: active ? 1 : 0.72,
              transition:
                'opacity 0.2s ease, background 0.22s ease, box-shadow 0.22s ease, transform 0.18s ease',
              '&:hover': {
                opacity: active ? 1 : 0.92,
                transform: active ? 'none' : 'translateY(-1px)',
              },
              '&:active': {
                transform: 'scale(0.96)',
              },
            }}
          >
            <Box
              component="img"
              src={src}
              alt=""
              loading="eager"
              decoding="async"
              draggable={false}
              sx={{
                height: '1.22rem',
                width: '1.22rem',
                aspectRatio: '1 / 1',
                objectFit: 'cover',
                borderRadius: '50%',
                pointerEvents: 'none',
                display: 'block',
                filter: active ? 'none' : 'saturate(0.82) brightness(0.92)',
                transition: 'filter 0.22s ease',
              }}
            />
          </ButtonBase>
        );
      })}
    </Box>
  );
}

LanguageSwitcher.propTypes = {
  darkMode: PropTypes.bool,
  sx: PropTypes.object,
};

LanguageSwitcher.defaultProps = {
  darkMode: false,
  sx: {},
};

export default LanguageSwitcher;
